/**
 * Project Row Component
 * Table row view for a project
 * Aviation Dashboard Style
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { type DBProject } from '../../db';
import { projectsApi } from '../../lib/api';

interface ProjectRowProps {
  project: DBProject;
  onDelete?: (projectId: string) => void;
}

// Aviation status badge
function AviationStatusBadge({ status }: { status: string }) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'EXPORTED':
        return 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30';
      case 'IN_PROGRESS':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
      case 'PENDING':
        return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
      default:
        return 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30';
    }
  };

  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono font-medium uppercase tracking-wider rounded border ${getStatusStyle(status)}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function ProjectRow({ project, onDelete }: ProjectRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm(`Delete project "${project.code}"? This will mark it for deletion by an administrator.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await projectsApi.delete(project.id);
      toast.success('Project marked for deletion');
      // Dispatch custom event to notify all components
      window.dispatchEvent(new CustomEvent('projects-updated'));
      onDelete?.(project.id);
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project', {
        description: 'Please try again',
      });
    } finally {
      setIsDeleting(false);
      setMenuOpen(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
      <td className="px-4 py-3">
        <Link 
          to={`/projects/${project.id}`}
          className="block"
        >
          <div className="font-mono font-medium text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
            {project.code}
          </div>
          <div className="text-xs font-mono text-slate-500">{project.name}</div>
          <div className="text-[10px] font-mono text-slate-400 dark:text-slate-600 mt-0.5 tabular-nums">
            CRT :: {format(new Date(project.createdAt), "yyyy.MM.dd")}
          </div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-mono text-slate-500 dark:text-slate-400">{project.line || '-'}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-mono text-slate-700 dark:text-slate-300 tabular-nums">{project.photoCount}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-mono text-slate-700 dark:text-slate-300 tabular-nums">{project.anomalyCount}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex flex-col items-center gap-1">
          <AviationStatusBadge status={project.status} />
          <div className="w-20 progress-bar-aviation h-1">
            <div 
              className="progress-fill" 
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="relative inline-block">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4 text-slate-500" />
          </button>

          {menuOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setMenuOpen(false)} 
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800/95 backdrop-blur rounded-lg shadow-lg border border-slate-200 dark:border-slate-700/50 z-50 py-1">
                <Link
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                  onClick={() => setMenuOpen(false)}
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </Link>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-mono text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
